import { atom } from 'jotai';

import {
  clearQueue,
  enqueueAudio,
  markAudioCompleted,
  updateAudioInQueue,
} from './project-response-audio-queue.helpers';
import {
  AudioQueueItem,
  AudioQueueState,
} from './project-response-audio-queue.types';

// Base atom for audio queue state - centralized controller manages this
const audioQueueStateAtom = atom<AudioQueueState>({
  queue: [],
  currentPlayingId: null,
});

// Read-only atom to get the current queue array
export const audioQueueAtom = atom((get) => get(audioQueueStateAtom).queue);

// Write-only atom to enqueue new audio
export const enqueueAudioAtom = atom(null, (get, set, item: AudioQueueItem) => {
  const currentState = get(audioQueueStateAtom);
  const newState = enqueueAudio(currentState, item);
  set(audioQueueStateAtom, newState);
});

// Write-only atom to update audio in queue
export const updateAudioInQueueAtom = atom(
  null,
  (
    get,
    set,
    {
      messageId,
      updates,
    }: { messageId: string; updates: Partial<AudioQueueItem> }
  ) => {
    const currentState = get(audioQueueStateAtom);
    const newState = updateAudioInQueue(currentState, messageId, updates);
    set(audioQueueStateAtom, newState);
  }
);

// Write-only atom to mark audio as completed
export const markAudioCompletedAtom = atom(
  null,
  (get, set, messageId: string) => {
    const currentState = get(audioQueueStateAtom);
    const newState = markAudioCompleted(currentState, messageId);
    set(audioQueueStateAtom, newState);
  }
);

// Write-only atom to clear the queue
export const clearAudioQueueAtom = atom(null, (get, set) => {
  const newState = clearQueue();
  set(audioQueueStateAtom, newState);
});

// Write-only atom to play or enqueue audio (handles cases where queue was cleared)
export const playOrEnqueueAudioAtom = atom(
  null,
  (get, set, item: AudioQueueItem) => {
    const currentState = get(audioQueueStateAtom);
    const exists = currentState.queue.some(
      (q) => q.messageId === item.messageId
    );

    if (exists) {
      const newState = updateAudioInQueue(currentState, item.messageId, {
        status: 'ready',
        shouldInterrupt: true,
        ...(item.audioUrl ? { audioUrl: item.audioUrl } : {}),
      });
      set(audioQueueStateAtom, newState);
    } else {
      const newState = enqueueAudio(currentState, {
        ...item,
        status: 'ready',
        shouldInterrupt: true,
      });
      set(audioQueueStateAtom, newState);
    }
  }
);
